/**
 * Kiosk (/votar/:electionId): a volunteer helps a voter who cannot or does not want to use their own
 * phone. Search by CI (the number alone is enough) → check the photo and the physical CI → the
 * voter taps their candidate → confirm → next person. Proxy voting is allowed (ADB-139); the
 * "CI verificado" tick is stored with the vote.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Loader2, Search, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { castKioskVote, kioskLookup } from '../data/votingRepository';
import { useElectionState } from '../hooks/useVoting';
import { KioskBallot } from '../components/KioskBallot';
import { VoterPhoto, VoterStatus, VoterSummary } from '../components/KioskVoterCard';
import type { KioskVoter } from '../types';
import { votingErrorMessage } from '../utils/votingErrors';

const MIN_QUERY = 3;
const DONE_SECONDS = 4;

export function KioskVotePage() {
  const { electionId } = useParams<{ electionId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const { can, isLoading } = usePermissions();

  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (isLoading) return <FullScreen><Loader2 className="size-8 animate-spin text-muted-foreground" /></FullScreen>;
  if (!can('vote.proxy')) {
    return (
      <FullScreen>
        <ShieldAlert className="size-10 text-muted-foreground" />
        <p className="text-muted-foreground">Su usuario no tiene permiso para votar en nombre de otras personas.</p>
        <Link to="/voting" className="text-sm underline">Volver</Link>
      </FullScreen>
    );
  }
  return <KioskScreen electionId={electionId as string} />;
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">{children}</div>;
}

type Step = 'search' | 'verify' | 'ballot' | 'done';

function KioskScreen({ electionId }: { electionId: string }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useElectionState(electionId);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [step, setStep] = useState<Step>('search');
  const [voter, setVoter] = useState<KioskVoter | null>(null);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const [lastName, setLastName] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(input.trim()), 400);
    return () => clearTimeout(t);
  }, [input]);

  const state = data?.state;
  const race = useMemo(() => state?.races.find((r) => r.status === 'OPEN') ?? null, [state]);
  const positionTitle = state?.positions.find((p) => p.id === race?.position_id)?.title ?? '';

  const { data: matches = [], isFetching, error: lookupError } = useQuery({
    queryKey: ['voting', 'kiosk-lookup', electionId, query, race?.id ?? null],
    queryFn: () => kioskLookup(electionId, query),
    enabled: step === 'search' && query.length >= MIN_QUERY,
  });

  const reset = () => {
    setStep('search');
    setVoter(null);
    setVerified(false);
    setVoteError(null);
    setInput('');
    setQuery('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (step !== 'done') return;
    const t = setTimeout(reset, DONE_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [step]);

  // The round closed while a voter was choosing
  useEffect(() => {
    if (!race && (step === 'ballot' || step === 'verify')) reset();
  }, [race, step]);

  const vote = async (candidateId: string | null) => {
    if (!voter || !race || submitting) return;
    setSubmitting(true);
    setVoteError(null);
    try {
      await castKioskVote({ raceId: race.id, participantId: voter.participant_id, voterId: voter.voter_id, candidateId });
      setLastName(voter.full_name);
      setStep('done');
      void qc.invalidateQueries({ queryKey: ['voting', 'kiosk-lookup', electionId] });
    } catch (err) {
      const msg = votingErrorMessage(err);
      const offline = err instanceof TypeError || /fetch|network/i.test(String(err));
      setVoteError(offline ? 'Sin conexión. Verifique la señal e intente de nuevo; su voto aún no se registró.' : msg);
      // Cases where the ballot cannot continue
      if (/ya votó|cerrada|no puede votar|no está/i.test(msg)) setTimeout(reset, 3500);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <FullScreen><Loader2 className="size-8 animate-spin text-muted-foreground" /></FullScreen>;
  if (error || !state) {
    return <FullScreen><p className="text-destructive">{votingErrorMessage(error)}</p><Link to="/voting" className="text-sm underline">Volver</Link></FullScreen>;
  }

  if (step === 'done') {
    return (
      <FullScreen>
        <CheckCircle2 className="size-32 text-green-600" />
        <p className="text-5xl font-bold">Voto registrado</p>
        <p className="text-2xl text-muted-foreground">{lastName}</p>
        <Button size="lg" className="mt-4 h-14 text-lg" onClick={reset}>Siguiente persona</Button>
      </FullScreen>
    );
  }

  if (!race) {
    return (
      <FullScreen>
        <p className="text-4xl font-bold">No hay votación abierta</p>
        <p className="text-xl text-muted-foreground">{state.election.name}</p>
        <p className="text-muted-foreground">Esta pantalla se actualiza sola cuando se abra una ronda.</p>
        <Link to={`/voting/${electionId}`} className="text-sm underline">Ir a la administración</Link>
      </FullScreen>
    );
  }

  if (step === 'ballot' && voter) {
    return (
      <KioskBallot
        state={state}
        race={race}
        positionTitle={positionTitle}
        voterName={voter.full_name}
        submitting={submitting}
        error={voteError}
        onVote={(id) => void vote(id)}
        onCancel={reset}
      />
    );
  }

  if (step === 'verify' && voter) {
    const canContinue = voter.eligible && !voter.voted;
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col justify-center gap-6 p-6">
        <Button variant="ghost" className="w-fit text-lg" onClick={reset}><ArrowLeft className="mr-2 size-5" /> Buscar a otra persona</Button>
        <div className="space-y-5 rounded-3xl border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-5">
            <VoterPhoto voter={voter} electionId={electionId} className="size-40" />
            <div className="space-y-2">
              <VoterSummary voter={voter} />
              <p className="text-lg"><VoterStatus voter={voter} /></p>
            </div>
          </div>
          {canContinue && (
            <>
              <label className="flex cursor-pointer items-center gap-4 rounded-xl border-2 border-primary/40 p-4 text-xl">
                <Checkbox className="size-7" checked={verified} onCheckedChange={(v) => setVerified(v === true)} />
                Comparé la foto y el CI de la persona
              </label>
              <Button size="lg" className="h-16 w-full text-xl" disabled={!verified} onClick={() => setStep('ballot')}>
                Continuar a la boleta
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col justify-center gap-6 p-6">
      <div className="text-center">
        <p className="text-lg text-muted-foreground">{state.election.name}</p>
        <h1 className="text-4xl font-bold">{positionTitle} · Ronda {race.round_number}</h1>
        <p className="text-xl text-muted-foreground">Escriba el CI de la persona que va a votar</p>
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 size-6 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={searchRef}
          autoFocus
          inputMode="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="CI, código ADB o nombre"
          className="h-16 pl-14 text-2xl"
        />
        {isFetching && <Loader2 className="absolute right-4 top-1/2 size-5 -translate-y-1/2 animate-spin" />}
      </div>

      {lookupError && <p className="text-center text-destructive">{votingErrorMessage(lookupError)}</p>}
      {query.length >= MIN_QUERY && !isFetching && !lookupError && matches.length === 0 && (
        <p className="text-center text-xl text-muted-foreground">No se encontró a nadie con ese dato.</p>
      )}

      <ul className="space-y-3">
        {matches.map((m) => (
          <li key={`${m.participant_id}-${m.voter_id}`}>
            <button
              type="button"
              onClick={() => { setVoter(m); setVerified(false); setVoteError(null); setStep('verify'); }}
              className="flex w-full items-center gap-4 rounded-2xl border bg-card p-4 text-left shadow-sm transition hover:border-primary hover:shadow-md"
            >
              <VoterPhoto voter={m} electionId={electionId} className="size-20" />
              <div className="min-w-0 flex-1 space-y-1">
                <VoterSummary voter={m} />
                <p className="text-base"><VoterStatus voter={m} /></p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
